import { Injectable } from '@angular/core';
import { LabelingEntry } from '../labeling/labelResultEntry';
import { AuthService } from './auth.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { DocHistory } from '../model/docHistory';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { MessageService } from './message.service';
import { EnvService } from '../../environments/env.service';

export interface PdfHeader {
  url: string;
  httpHeaders: {
    Accept: string;
    Authorization?: string;
    'X-Authorization'?: string;
  };
}

@Injectable({ providedIn: 'root' })
export class DbAccessService {
  completeToken = null;
  apiToken = '';
  author = '';
  error = '';
  docHistory: Array<DocHistory> = [];
  private backendUrl: string;

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    private messageService: MessageService,
    private envService: EnvService,
  ) {
    this.init();
  }

  init() {
    this.backendUrl = this.envService.getMetadataEndpoint();
    if (this.authService.currentUser) {
      this.completeToken = localStorage.getItem('token');
      const jwt = new JwtHelperService();
      const jwtToken = jwt.decodeToken(this.completeToken);
      this.apiToken = jwtToken.ep_jwt;
      // this.author = jwtToken.author;
    } else {
      this.authService.logout();
    }
  }

  async stageNewDocument(): Promise<LabelingEntry | null> {
    for await (const newElem of await firstValueFrom(
      this.http.get<LabelingEntry[]>(this.backendUrl, { params: { state: 'new' }, withCredentials: true }),
    )) {
      const inProgressElem = { ...newElem };
      inProgressElem.state = 'inProgress';
      inProgressElem.author = this.authService.author;
      firstValueFrom(this.http.put(this.backendUrl, inProgressElem))
        .then(() => {
          // The post has been created!
        })
        .catch((error) => {
          this.messageService.add(`Fehler beim Erzeugen eines neuen Dokumentes: ${error}`, 'error');
        });
      firstValueFrom(
        this.http.delete(`${this.backendUrl}/${encodeURIComponent(inProgressElem.doc_uri)}?state=new`, { withCredentials: true }),
      )
        .then(() => {
          // The post has been deleted!
        })
        .catch((error) => {
          this.messageService.add(`Fehler beim Löschen eines Dokumentes: ${error}`, 'error');
        });
      return inProgressElem;
    }
    return null;
  }

  async selectDocumentFromUri(uri: string, state?: string): Promise<LabelingEntry> {
    let status = 'done';
    if (state) {
      status = state;
    }
    for await (const elem of await firstValueFrom(
      this.http.get<LabelingEntry[]>(this.backendUrl, { params: { state: status, doc_uri: uri }, withCredentials: true }),
    )) {
      return elem;
    }
    return null;
  }

  async selectInProgressDocument(): Promise<LabelingEntry | null> {
    for await (const elem of await firstValueFrom(
      this.http.get<LabelingEntry[]>(this.backendUrl, { params: { state: 'inProgress' }, withCredentials: true }),
    )) {
      if (elem.author === this.authService.author) {
        return elem;
      }
    }
    return null;
  }

  async selectInReviewDocument(): Promise<LabelingEntry | null> {
    for await (const elem of await firstValueFrom(
      this.http.get<LabelingEntry[]>(this.backendUrl, { params: { state: 'review' }, withCredentials: true }),
    )) {
      return elem;
    }
    return null;
  }

  async selectDoneDocuments(): Promise<LabelingEntry | null> {
    this.docHistory = [];

    if (this.authService.getAuthor() === 'Reviewer') {
      for await (const elem of await firstValueFrom(
        this.http.get<LabelingEntry[]>(this.backendUrl, { params: { state: 'review' }, withCredentials: true }),
      )) {
        this.createHistoryEntries(elem);
      }
    } else {
      // get list of all done docs
      for await (const elem of await firstValueFrom(
        this.http.get<LabelingEntry[]>(this.backendUrl, {
          params: { state: 'done', author: this.authService.getAuthor() },
          withCredentials: true,
        }),
      )) {
        this.createHistoryEntries(elem);
      }
      // get list of all review docs
      for await (const elem of await firstValueFrom(
        this.http.get<LabelingEntry[]>(this.backendUrl, {
          params: { state: 'review', author: this.authService.getAuthor() },
          withCredentials: true,
        }),
      )) {
        this.createHistoryEntries(elem);
      }
    }

    return null;
  }

  createHistoryEntries(elem: LabelingEntry): void {
    const historyEntry = new DocHistory();
    const options: Intl.DateTimeFormatOptions = { month: '2-digit', day: '2-digit' };
    const postedAt = new Date(elem.postedAt * 1000);
    const timeString = postedAt.toLocaleTimeString();
    const dateString = postedAt.toLocaleDateString('de-DE', options);
    historyEntry.doc_name = `${dateString}, ${timeString}`;
    historyEntry.doc_uri = elem.doc_uri;
    historyEntry.state = elem.state;
    historyEntry.timestamp = postedAt;
    this.docHistory.push(historyEntry);
  }

  createDocumentLocation(url: string): PdfHeader {
    const headerObj = {
      url: url,
      httpHeaders: {
        Accept: 'application/pdf',
        Authorization: `Bearer ${this.apiToken}`,
      },
      withCredentials: true,
    };

    if (url.includes('labelingtool-backend.smartcat.ep.aws.it-hps.de')) {
      delete headerObj.httpHeaders.Authorization;
      headerObj.httpHeaders['X-Authentication'] = this.completeToken;
      return headerObj;
    }
    return headerObj;
  }

  retrievePdfObject(url: string): Observable<ArrayBuffer> {
    const headerObj = this.createDocumentLocation(url);
    const header = headerObj.httpHeaders; // console.log('URL: ', url, 'HTTP_HEADERS:', header);

    return this.http.get(url, { headers: header, responseType: 'arraybuffer', withCredentials: true });
  }

  deleteItem(currentDBEntry: LabelingEntry): Promise<any> {
    return firstValueFrom(
      this.http.delete(`${this.backendUrl}/${encodeURIComponent(currentDBEntry.doc_uri)}?state=${currentDBEntry.state}`, {
        withCredentials: true,
      }),
    ).catch((error) => {
      this.messageService.add(`Fehler beim Löschen eines Dokumentes: ${error}`, 'error');
    });
  }

  putItem(entry: LabelingEntry): Promise<any> {
    return firstValueFrom(this.http.put(this.backendUrl, entry, { withCredentials: true })).catch((error) => {
      this.messageService.add(`Fehler beim Löschen eines Dokumentes: ${error}`, 'error');
    });
  }

  getParamDocId(url: string): string | null {
    const queryString = url.split('?');
    let docId = '';
    docId = new URLSearchParams(queryString[1]).get('doc_id');
    if (docId !== null) {
      return docId;
    }
    return null;
  }
}
